import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { PageHeader, ChipFilter, StatusBadge, Modal, MetricCard, Empty, ProgressBar, ImportDropZone, Alert } from '../components/UI';

const fmt = n => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

// ═══════════════════════════════════════════════════════════════════════════════
// MRQ LOG
// ═══════════════════════════════════════════════════════════════════════════════
const EMPTY_MRQ = { date: '', ref: '', item: '', qty: 0, unit: 'm³', specRef: '', wbs: '', priority: 'Normal', status: 'Draft', supplier: '', deliveryDate: '', cost: 0, notes: '' };

export function MRQLog() {
  const { data, activeProject, upsert, deleteItem } = useData();
  const { can } = useAuth();
  const [filter, setFilter] = useState('All');
  const [form, setForm] = useState(null);

  const all = data.mrqs.filter(m => m.projectId === activeProject?.id);
  const filtered = filter === 'All' ? all : all.filter(m => m.status === filter);
  const nextRef = () => 'MRQ-' + String(all.length + 1).padStart(3, '0');
  const save = () => { upsert('mrqs', { ...form, projectId: activeProject.id }); setForm(null); };

  return (
    <div className="page-content">
      <PageHeader title="Material Requisition Log" subtitle={activeProject?.name}>
        {can('canCreate') && <button className="btn btn-primary" onClick={() => setForm({ ...EMPTY_MRQ, ref: nextRef(), id: null })}>+ New MRQ</button>}
      </PageHeader>
      <div className="grid-4 mb-20">
        <MetricCard label="Total MRQs" value={all.length} />
        <MetricCard label="Pending / Draft" value={all.filter(m => m.status === 'Pending' || m.status === 'Draft').length} color="var(--amber)" />
        <MetricCard label="Ordered" value={all.filter(m => m.status === 'Ordered').length} color="var(--blue-text)" />
        <MetricCard label="Est. Total Cost" value={fmt(all.reduce((a, m) => a + m.cost, 0))} />
      </div>
      <ChipFilter options={['All', 'Draft', 'Pending', 'Approved', 'Ordered', 'Received']} value={filter} onChange={setFilter} />
      {filtered.length === 0 ? <Empty icon="📦" message="No requisitions match this filter." /> : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Ref</th><th>Date</th><th>Item</th><th>Qty</th><th>Unit</th><th>WBS</th><th>Priority</th><th>Supplier</th><th>Est. Cost</th><th>Status</th>{can('canEdit') && <th></th>}</tr></thead>
            <tbody>
              {filtered.map(m => (
                <tr key={m.id} className="clickable" onClick={() => setForm({ ...m })}>
                  <td className="td-mono">{m.ref}</td><td>{m.date}</td>
                  <td className="td-primary">{m.item}</td>
                  <td>{m.qty}</td><td>{m.unit}</td>
                  <td><span className="tag">{m.wbs}</span></td>
                  <td><StatusBadge value={m.priority} /></td>
                  <td className="td-muted">{m.supplier || '—'}</td>
                  <td>{m.cost ? fmt(m.cost) : '—'}</td>
                  <td><StatusBadge value={m.status} /></td>
                  {can('canEdit') && <td onClick={e => e.stopPropagation()}><button className="btn btn-danger btn-xs" onClick={() => deleteItem('mrqs', m.id)}>Del</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {form && (
        <Modal title={form.id ? 'Edit MRQ' : 'New Material Requisition'} onClose={() => setForm(null)} size="modal-lg">
          <div className="form-grid">
            <div className="form-group"><label>Ref</label><input value={form.ref} onChange={e => setForm({ ...form, ref: e.target.value })} /></div>
            <div className="form-group"><label>Date</label><input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div className="form-group form-full"><label>Material / Item Description</label><input value={form.item} onChange={e => setForm({ ...form, item: e.target.value })} /></div>
            <div className="form-group"><label>Quantity</label><input type="number" value={form.qty} onChange={e => setForm({ ...form, qty: +e.target.value })} /></div>
            <div className="form-group"><label>Unit</label>
              <select value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })}>
                {['m³','m²','m','tons','kg','pcs','sets','lump sum'].map(u => <option key={u}>{u}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Spec Ref</label><input value={form.specRef} onChange={e => setForm({ ...form, specRef: e.target.value })} /></div>
            <div className="form-group"><label>WBS Item</label><input value={form.wbs} onChange={e => setForm({ ...form, wbs: e.target.value })} /></div>
            <div className="form-group"><label>Priority</label>
              <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                {['Urgent','Normal','Low'].map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                {['Draft','Pending','Approved','Ordered','Received','Rejected'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Supplier</label><input value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} /></div>
            <div className="form-group"><label>Required Delivery Date</label><input type="date" value={form.deliveryDate} onChange={e => setForm({ ...form, deliveryDate: e.target.value })} /></div>
            <div className="form-group"><label>Estimated Cost (USD)</label><input type="number" value={form.cost} onChange={e => setForm({ ...form, cost: +e.target.value })} /></div>
            <div className="form-group form-full"><label>Notes</label><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={() => setForm(null)}>Cancel</button>
            {can('canEdit') && <button className="btn btn-primary" onClick={save}>Save MRQ</button>}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// VARIATION ORDERS
// ═══════════════════════════════════════════════════════════════════════════════
const EMPTY_VO = { date: '', ref: '', title: '', scope: '', contractRef: '', category: 'Client Variation', status: 'Pending', value: 0, submittedBy: '', approvedBy: '', notes: '' };

export function VOLog() {
  const { data, activeProject, upsert, deleteItem } = useData();
  const { can } = useAuth();
  const [filter, setFilter] = useState('All');
  const [form, setForm] = useState(null);

  const all = data.vos.filter(v => v.projectId === activeProject?.id);
  const filtered = filter === 'All' ? all : all.filter(v => v.status === filter);
  const nextRef = () => 'VO-' + String(all.length + 1).padStart(3, '0');
  const save = () => { upsert('vos', { ...form, projectId: activeProject.id }); setForm(null); };

  const approved  = all.filter(v => v.status === 'Approved').reduce((a, v) => a + v.value, 0);
  const pending   = all.filter(v => v.status !== 'Approved' && v.status !== 'Rejected').reduce((a, v) => a + v.value, 0);

  return (
    <div className="page-content">
      <PageHeader title="Variation Orders" subtitle={activeProject?.name}>
        {can('canCreate') && <button className="btn btn-primary" onClick={() => setForm({ ...EMPTY_VO, ref: nextRef(), contractRef: activeProject?.contractRef || '', id: null })}>+ New VO</button>}
      </PageHeader>
      <div className="grid-4 mb-20">
        <MetricCard label="Total VOs" value={all.length} />
        <MetricCard label="Approved Value" value={fmt(approved)} color="var(--green)" />
        <MetricCard label="Pending Value" value={fmt(pending)} color="var(--amber)" />
        <MetricCard label="Rejected" value={all.filter(v => v.status === 'Rejected').length} color="var(--red)" />
      </div>
      <ChipFilter options={['All','Pending','Under Review','Approved','Rejected']} value={filter} onChange={setFilter} />
      {filtered.length === 0 ? <Empty icon="△" message="No variation orders found." /> : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Ref</th><th>Date</th><th>Title</th><th>Category</th><th>Value</th><th>Submitted By</th><th>Approved By</th><th>Status</th>{can('canEdit') && <th></th>}</tr></thead>
            <tbody>
              {filtered.map(v => (
                <tr key={v.id} className="clickable" onClick={() => setForm({ ...v })}>
                  <td className="td-mono" style={{ color: 'var(--purple)' }}>{v.ref}</td>
                  <td>{v.date}</td>
                  <td className="td-primary" style={{ maxWidth: 200 }} >{v.title}</td>
                  <td><span className="tag">{v.category}</span></td>
                  <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{fmt(v.value)}</td>
                  <td className="td-muted">{v.submittedBy}</td>
                  <td className="td-muted">{v.approvedBy || '—'}</td>
                  <td><StatusBadge value={v.status} /></td>
                  {can('canEdit') && <td onClick={e => e.stopPropagation()}><button className="btn btn-danger btn-xs" onClick={() => deleteItem('vos', v.id)}>Del</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {form && (
        <Modal title={form.id ? 'Edit VO' : 'New Variation Order'} onClose={() => setForm(null)} size="modal-lg">
          <div className="form-grid">
            <div className="form-group"><label>Ref</label><input value={form.ref} onChange={e => setForm({ ...form, ref: e.target.value })} /></div>
            <div className="form-group"><label>Date</label><input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div className="form-group form-full"><label>Title</label><input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
            <div className="form-group form-full"><label>Scope of Work</label><textarea rows={3} value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value })} /></div>
            <div className="form-group"><label>Category</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                {['Client Variation','Design Change','Engineering Change','Site Instruction','Provisional Sum'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Contract Ref</label><input value={form.contractRef} onChange={e => setForm({ ...form, contractRef: e.target.value })} /></div>
            <div className="form-group"><label>Value (USD)</label><input type="number" value={form.value} onChange={e => setForm({ ...form, value: +e.target.value })} /></div>
            <div className="form-group"><label>Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                {['Pending','Under Review','Approved','Rejected'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Submitted By</label><input value={form.submittedBy} onChange={e => setForm({ ...form, submittedBy: e.target.value })} /></div>
            <div className="form-group"><label>Approved By</label><input value={form.approvedBy} onChange={e => setForm({ ...form, approvedBy: e.target.value })} /></div>
            <div className="form-group form-full"><label>Notes</label><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={() => setForm(null)}>Cancel</button>
            {can('canEdit') && <button className="btn btn-primary" onClick={save}>Save VO</button>}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COST TRACKER
// ═══════════════════════════════════════════════════════════════════════════════
const EMPTY_COST = { category: '', wbs: '', budgeted: 0, committed: 0, actual: 0 };

export function CostTracker() {
  const { data, activeProject, upsert, deleteItem } = useData();
  const { can } = useAuth();
  const [form, setForm] = useState(null);

  const costs = data.costs.filter(c => c.projectId === activeProject?.id);
  const totB = costs.reduce((a, c) => a + c.budgeted, 0);
  const totC = costs.reduce((a, c) => a + c.committed, 0);
  const totA = costs.reduce((a, c) => a + c.actual, 0);
  const save = () => { upsert('costs', { ...form, projectId: activeProject.id }); setForm(null); };

  return (
    <div className="page-content">
      <PageHeader title="Cost Tracker" subtitle={activeProject?.name}>
        {can('canCreate') && <button className="btn btn-primary" onClick={() => setForm({ ...EMPTY_COST, id: null })}>+ Add Line</button>}
      </PageHeader>
      <div className="grid-4 mb-20">
        <MetricCard label="Contract Budget" value={fmt(totB)} />
        <MetricCard label="Committed" value={fmt(totC)} color="var(--blue-text)" sub={`${totB ? Math.round(totC/totB*100) : 0}% of budget`} />
        <MetricCard label="Actual Spend" value={fmt(totA)} color="var(--amber)" sub={`${totB ? Math.round(totA/totB*100) : 0}% of budget`} />
        <MetricCard label="Remaining" value={fmt(totB - totA)} color={totB - totA < 0 ? 'var(--red)' : 'var(--green)'} />
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>Category</th><th>WBS</th><th>Budgeted</th><th>Committed</th><th>Actual</th><th>Remaining</th><th>% Used</th><th>Variance (B–C)</th>{can('canEdit') && <th></th>}</tr></thead>
          <tbody>
            {costs.map(c => {
              const rem = c.budgeted - c.actual;
              const pct = c.budgeted ? Math.round(c.actual / c.budgeted * 100) : 0;
              const variance = c.budgeted - c.committed;
              return (
                <tr key={c.id} className="clickable" onClick={() => setForm({ ...c })}>
                  <td className="td-primary">{c.category}</td>
                  <td><span className="tag">{c.wbs}</span></td>
                  <td>{fmt(c.budgeted)}</td>
                  <td style={{ color: 'var(--blue-text)' }}>{fmt(c.committed)}</td>
                  <td style={{ color: 'var(--amber)' }}>{fmt(c.actual)}</td>
                  <td style={{ color: rem < 0 ? 'var(--red)' : 'var(--green)', fontWeight: 600 }}>{fmt(rem)}</td>
                  <td style={{ minWidth: 140 }}><ProgressBar value={c.actual} max={c.budgeted} /></td>
                  <td style={{ color: variance < 0 ? 'var(--red)' : 'var(--green)' }}>{fmt(variance)}</td>
                  {can('canEdit') && <td onClick={e => e.stopPropagation()}><button className="btn btn-danger btn-xs" onClick={() => deleteItem('costs', c.id)}>Del</button></td>}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2}>TOTAL</td>
              <td>{fmt(totB)}</td>
              <td style={{ color: 'var(--blue-text)' }}>{fmt(totC)}</td>
              <td style={{ color: 'var(--amber)' }}>{fmt(totA)}</td>
              <td style={{ color: totB - totA < 0 ? 'var(--red)' : 'var(--green)' }}>{fmt(totB - totA)}</td>
              <td>{totB ? Math.round(totA / totB * 100) : 0}%</td>
              <td style={{ color: totB - totC < 0 ? 'var(--red)' : 'var(--green)' }}>{fmt(totB - totC)}</td>
              {can('canEdit') && <td />}
            </tr>
          </tfoot>
        </table>
      </div>
      {form && (
        <Modal title={form.id ? 'Edit Cost Item' : 'New Cost Item'} onClose={() => setForm(null)}>
          <div className="form-grid">
            <div className="form-group form-full"><label>Category Name</label><input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} /></div>
            <div className="form-group"><label>WBS Ref</label><input value={form.wbs} onChange={e => setForm({ ...form, wbs: e.target.value })} /></div>
            <div className="form-group"><label>Budgeted (USD)</label><input type="number" value={form.budgeted} onChange={e => setForm({ ...form, budgeted: +e.target.value })} /></div>
            <div className="form-group"><label>Committed (USD)</label><input type="number" value={form.committed} onChange={e => setForm({ ...form, committed: +e.target.value })} /></div>
            <div className="form-group"><label>Actual Spend (USD)</label><input type="number" value={form.actual} onChange={e => setForm({ ...form, actual: +e.target.value })} /></div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={() => setForm(null)}>Cancel</button>
            {can('canEdit') && <button className="btn btn-primary" onClick={save}>Save</button>}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DOCUMENT LOG
// ═══════════════════════════════════════════════════════════════════════════════
const EMPTY_DOC = { date: '', ref: '', title: '', category: 'Drawing', discipline: 'Structural', status: 'For Review', rev: 'A', issuedBy: '', file: '', notes: '' };

export function DocumentLog() {
  const { data, activeProject, upsert, deleteItem } = useData();
  const { can } = useAuth();
  const [filter, setFilter] = useState('All');
  const [form, setForm] = useState(null);

  const all = data.docs.filter(d => d.projectId === activeProject?.id);
  const filtered = filter === 'All' ? all : all.filter(d => d.category === filter);
  const nextRef = () => 'DOC-' + String(all.length + 1).padStart(3, '0');
  const save = () => { upsert('docs', { ...form, projectId: activeProject.id }); setForm(null); };

  return (
    <div className="page-content">
      <PageHeader title="Document Log" subtitle={activeProject?.name}>
        {can('canCreate') && <button className="btn btn-primary" onClick={() => setForm({ ...EMPTY_DOC, ref: nextRef(), id: null })}>+ Add Document</button>}
      </PageHeader>
      <ChipFilter options={['All','Drawing','Submittal','Method Statement','RFI','Report','Specification','Contract']} value={filter} onChange={setFilter} />
      {filtered.length === 0 ? <Empty icon="📄" message="No documents found." /> : (
        <div className="table-wrap">
          <table>
            <thead><tr><th>Ref</th><th>Date</th><th>Title</th><th>Category</th><th>Discipline</th><th>Rev</th><th>Issued By</th><th>File</th><th>Status</th>{can('canEdit') && <th></th>}</tr></thead>
            <tbody>
              {filtered.map(d => (
                <tr key={d.id} className="clickable" onClick={() => setForm({ ...d })}>
                  <td className="td-mono">{d.ref}</td>
                  <td>{d.date}</td>
                  <td className="td-primary" style={{ maxWidth: 220 }}>{d.title}</td>
                  <td><span className="tag">{d.category}</span></td>
                  <td className="td-muted">{d.discipline}</td>
                  <td style={{ fontFamily: 'var(--mono)', color: 'var(--amber)' }}>{d.rev}</td>
                  <td className="td-muted">{d.issuedBy}</td>
                  <td className="td-muted" style={{ fontSize: 11 }}>{d.file}</td>
                  <td><StatusBadge value={d.status} /></td>
                  {can('canEdit') && <td onClick={e => e.stopPropagation()}><button className="btn btn-danger btn-xs" onClick={() => deleteItem('docs', d.id)}>Del</button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {form && (
        <Modal title={form.id ? 'Edit Document' : 'Add Document'} onClose={() => setForm(null)} size="modal-lg">
          <div className="form-grid">
            <div className="form-group"><label>Ref</label><input value={form.ref} onChange={e => setForm({ ...form, ref: e.target.value })} /></div>
            <div className="form-group"><label>Date</label><input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div className="form-group form-full"><label>Document Title</label><input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} /></div>
            <div className="form-group"><label>Category</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                {['Drawing','Submittal','Method Statement','RFI','Report','Specification','Contract','Shop Drawing'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Discipline</label>
              <select value={form.discipline} onChange={e => setForm({ ...form, discipline: e.target.value })}>
                {['Structural','Architectural','MEP','Civil','Geotechnical','Survey','Landscape'].map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Revision</label><input value={form.rev} onChange={e => setForm({ ...form, rev: e.target.value })} /></div>
            <div className="form-group"><label>Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                {['For Review','Under Review','Approved','Superseded','Rejected'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Issued By</label><input value={form.issuedBy} onChange={e => setForm({ ...form, issuedBy: e.target.value })} /></div>
            <div className="form-group"><label>Filename / Ref</label><input value={form.file} onChange={e => setForm({ ...form, file: e.target.value })} placeholder="e.g. STR-DWG-001.pdf" /></div>
            <div className="form-group form-full"><label>Notes</label><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={() => setForm(null)}>Cancel</button>
            {can('canEdit') && <button className="btn btn-primary" onClick={save}>Save Document</button>}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// IPC
// ═══════════════════════════════════════════════════════════════════════════════
const EMPTY_IPC = { ref: '', subcontractor: '', wbs: '', period: '', submittedDate: '', certifiedDate: '', claimed: 0, certified: 0, retention: 0, netPayable: 0, status: 'Under Review', notes: '' };

export function IPCLog() {
  const { data, activeProject, upsert, deleteItem } = useData();
  const { can } = useAuth();
  const [form, setForm] = useState(null);

  const all = data.ipcs.filter(i => i.projectId === activeProject?.id);
  const nextRef = () => 'IPC-' + String(all.length + 1).padStart(3, '0');
  const save = () => { upsert('ipcs', { ...form, projectId: activeProject.id }); setForm(null); };

  const totClaimed  = all.reduce((a, i) => a + i.claimed, 0);
  const totCert     = all.reduce((a, i) => a + i.certified, 0);
  const totPayable  = all.reduce((a, i) => a + i.netPayable, 0);

  return (
    <div className="page-content">
      <PageHeader title="IPC – Subcontractor Payments" subtitle={activeProject?.name}>
        {can('canCreate') && <button className="btn btn-primary" onClick={() => setForm({ ...EMPTY_IPC, ref: nextRef(), id: null })}>+ New IPC</button>}
      </PageHeader>
      <div className="grid-4 mb-20">
        <MetricCard label="Total IPCs" value={all.length} />
        <MetricCard label="Total Claimed" value={fmt(totClaimed)} />
        <MetricCard label="Total Certified" value={fmt(totCert)} color="var(--green)" />
        <MetricCard label="Net Payable" value={fmt(totPayable)} color="var(--blue-text)" />
      </div>
      {all.length === 0 ? <Empty icon="💳" message="No IPCs recorded yet." /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {all.map(ipc => (
            <div key={ipc.id} className="ipc-card">
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span className="td-mono" style={{ fontFamily: 'var(--mono)', color: 'var(--blue-text)' }}>{ipc.ref}</span>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{ipc.subcontractor}</span>
                    <span className="tag">{ipc.wbs}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                    Period: {ipc.period} · Submitted: {ipc.submittedDate}
                    {ipc.certifiedDate && ` · Certified: ${ipc.certifiedDate}`}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <StatusBadge value={ipc.status} />
                  {can('canEdit') && <button className="btn btn-ghost btn-sm" onClick={() => setForm({ ...ipc })}>Edit</button>}
                  {can('canDelete') && <button className="btn btn-danger btn-xs" onClick={() => deleteItem('ipcs', ipc.id)}>Del</button>}
                </div>
              </div>
              <div className="grid-4">
                {[['Claimed', ipc.claimed, ''], ['Certified', ipc.certified, 'var(--green)'], ['Retention', -ipc.retention, 'var(--red)'], ['Net Payable', ipc.netPayable, 'var(--blue-text)']].map(([l, v, c]) => (
                  <div key={l}>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{l}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: c || 'var(--text-primary)', marginTop: 2 }}>{fmt(v)}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {form && (
        <Modal title={form.id ? 'Edit IPC' : 'New IPC'} onClose={() => setForm(null)} size="modal-lg">
          <div className="form-grid">
            <div className="form-group"><label>Ref</label><input value={form.ref} onChange={e => setForm({ ...form, ref: e.target.value })} /></div>
            <div className="form-group"><label>Subcontractor</label><input value={form.subcontractor} onChange={e => setForm({ ...form, subcontractor: e.target.value })} /></div>
            <div className="form-group"><label>WBS Ref</label><input value={form.wbs} onChange={e => setForm({ ...form, wbs: e.target.value })} /></div>
            <div className="form-group"><label>Period</label><input value={form.period} onChange={e => setForm({ ...form, period: e.target.value })} placeholder="e.g. Nov 2025" /></div>
            <div className="form-group"><label>Submitted Date</label><input type="date" value={form.submittedDate} onChange={e => setForm({ ...form, submittedDate: e.target.value })} /></div>
            <div className="form-group"><label>Certified Date</label><input type="date" value={form.certifiedDate} onChange={e => setForm({ ...form, certifiedDate: e.target.value })} /></div>
            <div className="form-group"><label>Amount Claimed (USD)</label>
              <input type="number" value={form.claimed} onChange={e => setForm({ ...form, claimed: +e.target.value })} />
            </div>
            <div className="form-group"><label>Amount Certified (USD)</label>
              <input type="number" value={form.certified} onChange={e => {
                const v = +e.target.value;
                const ret = Math.round(v * 0.1);
                setForm({ ...form, certified: v, retention: ret, netPayable: v - ret });
              }} />
            </div>
            <div className="form-group"><label>Retention (USD)</label><input type="number" value={form.retention} onChange={e => setForm({ ...form, retention: +e.target.value, netPayable: form.certified - +e.target.value })} /></div>
            <div className="form-group"><label>Net Payable (USD)</label><input type="number" value={form.netPayable} onChange={e => setForm({ ...form, netPayable: +e.target.value })} /></div>
            <div className="form-group"><label>Status</label>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                {['Under Review','Certified','Paid','Disputed'].map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group form-full"><label>Notes</label><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={() => setForm(null)}>Cancel</button>
            {can('canEdit') && <button className="btn btn-primary" onClick={save}>Save IPC</button>}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DAILY REPORTS
// ═══════════════════════════════════════════════════════════════════════════════
const EMPTY_REPORT = { date: '', weather: '', wbs: '', activities: '', manpower: { labor: 0, supervisor: 0, engineer: 0 }, equipment: '', materials: '', issues: '', submittedBy: '' };

export function DailyReports() {
  const { data, activeProject, upsert, deleteItem } = useData();
  const { can } = useAuth();
  const [form, setForm] = useState(null);

  const all = [...data.dailyReports.filter(r => r.projectId === activeProject?.id)]
    .sort((a, b) => b.date.localeCompare(a.date));
  const save = () => { upsert('dailyReports', { ...form, projectId: activeProject.id }); setForm(null); };
  const wbsOptions = data.wbs.filter(w => w.projectId === activeProject?.id);

  return (
    <div className="page-content">
      <PageHeader title="Daily Reports" subtitle={`${all.length} reports · ${activeProject?.name}`}>
        {can('canCreate') && <button className="btn btn-primary" onClick={() => setForm({ ...EMPTY_REPORT, id: null })}>+ New Report</button>}
      </PageHeader>
      {all.length === 0 ? <Empty icon="📋" message="No daily reports yet." /> : (
        all.map(r => (
          <div key={r.id} className="report-card" onClick={() => setForm({ ...r })}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div className="report-date-box">
                  <div className="report-day">{new Date(r.date + 'T12:00').getDate()}</div>
                  <div className="report-month">{new Date(r.date + 'T12:00').toLocaleDateString('en', { month: 'short' })}</div>
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 2 }}>{r.date}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>☁ {r.weather} · WBS: {r.wbs}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6, maxWidth: 500 }}>{r.activities}</div>
                  {r.issues && r.issues !== 'None' && r.issues !== '' && (
                    <div style={{ marginTop: 8, background: 'var(--amber-dim)', border: '1px solid var(--amber-border)', borderRadius: 4, padding: '4px 10px', fontSize: 12, color: '#fcd34d' }}>
                      ⚠ {r.issues}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 20 }}>
                {[['Labor', r.manpower?.labor], ['Supervisors', r.manpower?.supervisor], ['Engineers', r.manpower?.engineer]].map(([l, v]) => (
                  <div key={l} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{l}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--blue-text)' }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-muted)' }}>
              Submitted by: {r.submittedBy}
              {r.materials && <> · Materials: {r.materials}</>}
            </div>
          </div>
        ))
      )}
      {form && (
        <Modal title={form.id ? 'Edit Daily Report' : 'New Daily Report'} onClose={() => setForm(null)} size="modal-lg">
          <div className="form-grid">
            <div className="form-group"><label>Date</label><input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
            <div className="form-group"><label>Weather</label><input value={form.weather} onChange={e => setForm({ ...form, weather: e.target.value })} placeholder="e.g. Sunny 24°C" /></div>
            <div className="form-group"><label>WBS Item</label>
              <select value={form.wbs} onChange={e => setForm({ ...form, wbs: e.target.value })}>
                <option value="">Select WBS...</option>
                {wbsOptions.map(w => <option key={w.id} value={w.code}>{w.code} – {w.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Submitted By</label><input value={form.submittedBy} onChange={e => setForm({ ...form, submittedBy: e.target.value })} /></div>
            <div className="form-group form-full"><label>Activities / Work Done</label><textarea rows={3} value={form.activities} onChange={e => setForm({ ...form, activities: e.target.value })} /></div>
          </div>
          <div className="form-section">
            <div className="form-section-title">Manpower</div>
            <div className="form-grid-3">
              <div className="form-group"><label>Labor</label><input type="number" value={form.manpower?.labor || 0} onChange={e => setForm({ ...form, manpower: { ...form.manpower, labor: +e.target.value } })} /></div>
              <div className="form-group"><label>Supervisors</label><input type="number" value={form.manpower?.supervisor || 0} onChange={e => setForm({ ...form, manpower: { ...form.manpower, supervisor: +e.target.value } })} /></div>
              <div className="form-group"><label>Engineers</label><input type="number" value={form.manpower?.engineer || 0} onChange={e => setForm({ ...form, manpower: { ...form.manpower, engineer: +e.target.value } })} /></div>
            </div>
          </div>
          <div className="form-group mb-12"><label>Equipment on Site</label><input value={form.equipment} onChange={e => setForm({ ...form, equipment: e.target.value })} placeholder="e.g. Tower Crane TC1, Concrete Pump P1" /></div>
          <div className="form-group mb-12"><label>Materials Received / Used</label><input value={form.materials} onChange={e => setForm({ ...form, materials: e.target.value })} /></div>
          <div className="form-group mb-12"><label>Issues / Delays</label><textarea rows={2} value={form.issues} onChange={e => setForm({ ...form, issues: e.target.value })} placeholder="None, or describe the issue..." /></div>
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={() => setForm(null)}>Cancel</button>
            {can('canEdit') && <button className="btn btn-primary" onClick={save}>Save Report</button>}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SETTINGS
// ═══════════════════════════════════════════════════════════════════════════════
export function Settings() {
  const { exportJSON, importJSON, resetToSeed } = useData();
  const { can } = useAuth();
  const [msg, setMsg] = useState('');

  const handleImport = async (file) => {
    try {
      await importJSON(file);
      setMsg('✅ Data imported successfully. All modules updated.');
    } catch (e) {
      setMsg('❌ Import failed: ' + e.message);
    }
  };

  return (
    <div className="page-content">
      <PageHeader title="Settings & Data Management" subtitle="Export, import, and manage your project data" />

      <div className="grid-2" style={{ gap: 24 }}>
        <div className="card">
          <h3 style={{ marginBottom: 6 }}>Export Data</h3>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
            Download all project data as a JSON file. Use this to back up your data or transfer it to another machine.
          </p>
          <button className="btn btn-primary" onClick={exportJSON}>📥 Download JSON Backup</button>
        </div>

        <div className="card">
          <h3 style={{ marginBottom: 6 }}>Import Data</h3>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
            Import a previously exported JSON file. <strong style={{ color: 'var(--amber)' }}>This will overwrite all current data.</strong>
          </p>
          <ImportDropZone onFile={handleImport} label="Drop exported JSON here or click to browse" />
          {msg && <div style={{ marginTop: 12, fontSize: 13, color: msg.startsWith('✅') ? 'var(--green)' : 'var(--red)' }}>{msg}</div>}
        </div>

        <div className="card">
          <h3 style={{ marginBottom: 6 }}>Role Permissions</h3>
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table>
              <thead><tr><th>Permission</th><th>Admin</th><th>PM</th><th>Engineer</th><th>Viewer</th></tr></thead>
              <tbody>
                {[['Create records','✓','✓','✓','—'],['Edit records','✓','✓','✓','—'],['Delete records','✓','—','—','—'],['Approve (VO/IPC)','✓','✓','—','—'],['Export data','✓','✓','✓','✓'],['Manage users','✓','—','—','—']].map(([p,...roles]) => (
                  <tr key={p}>
                    <td className="td-primary">{p}</td>
                    {roles.map((r, i) => <td key={i} style={{ color: r === '✓' ? 'var(--green)' : 'var(--text-muted)', fontWeight: r === '✓' ? 600 : 400 }}>{r}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <h3 style={{ marginBottom: 6 }}>Demo Accounts</h3>
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table>
              <thead><tr><th>Role</th><th>Email</th><th>Password</th></tr></thead>
              <tbody>
                {[['Admin','admin@constructpro.app','admin1234'],['PM','pm@constructpro.app','pm1234'],['Engineer','eng@constructpro.app','eng1234'],['Viewer','viewer@constructpro.app','view1234']].map(([r,e,p]) => (
                  <tr key={r}><td className="td-primary">{r}</td><td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{e}</td><td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{p}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
            To add real users, replace the USERS array in <code style={{ background: 'var(--bg-elevated)', padding: '1px 5px', borderRadius: 3, fontFamily: 'var(--mono)' }}>src/context/AuthContext.js</code> with a real authentication API.
          </p>
        </div>
      </div>

      {can('canDelete') && (
        <div className="card mt-20" style={{ borderColor: 'var(--red-border)' }}>
          <h3 style={{ color: 'var(--red)', marginBottom: 6 }}>Danger Zone</h3>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14 }}>Reset all data to demo seed data. This cannot be undone.</p>
          <button className="btn btn-danger" onClick={() => { if (window.confirm('Reset all data to demo? This cannot be undone.')) resetToSeed(); }}>
            ⚠ Reset to Demo Data
          </button>
        </div>
      )}
    </div>
  );
}
